import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  createRbacFeature,
  createRbacRole,
  deleteRbacFeature,
  fetchRbacFeatures,
  fetchRbacRoles,
  fetchRoleFeatures,
  setRoleFeatures,
  updateRbacFeature,
  updateRbacRole,
} from '../api/rbac.api.ts';
import { getErrorMessage } from '../utils/errorUtils.ts';
import { adminQueryKeys } from './queryKeys.ts';

export function useRbacRoles(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: adminQueryKeys.rbac.roles(),
    queryFn: fetchRbacRoles,
    enabled: options?.enabled ?? true,
  });
}

export function useRbacFeatures() {
  return useQuery({
    queryKey: adminQueryKeys.rbac.features(),
    queryFn: fetchRbacFeatures,
  });
}

export function useRoleFeatures(roleKey?: string) {
  return useQuery({
    queryKey: adminQueryKeys.rbac.roleFeatures(roleKey ?? ''),
    queryFn: () => fetchRoleFeatures(roleKey!),
    enabled: Boolean(roleKey),
  });
}

export function useSetRoleFeatures() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ roleKey, featureKeys }: { roleKey: string; featureKeys: string[] }) =>
      setRoleFeatures(roleKey, featureKeys),
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.rbac.roleFeatures(vars.roleKey) });
      toast.success('Role module access updated');
    },
    onError: (error) => toast.error(getErrorMessage(error, 'Failed to update role modules')),
  });
}

export function useCreateRbacRole() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createRbacRole,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.rbac.roles() });
      toast.success('Role created');
    },
    onError: (error) => toast.error(getErrorMessage(error, 'Failed to create role')),
  });
}

export function useUpdateRbacRole() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ roleKey, payload }: { roleKey: string; payload: { name?: string; description?: string } }) =>
      updateRbacRole(roleKey, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.rbac.roles() });
      toast.success('Role updated');
    },
    onError: (error) => toast.error(getErrorMessage(error, 'Failed to update role')),
  });
}

export function useCreateRbacFeature() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createRbacFeature,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.rbac.features() });
      toast.success('Module created');
    },
    onError: (error) => toast.error(getErrorMessage(error, 'Failed to create module')),
  });
}

export function useUpdateRbacFeature() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      featureKey,
      payload,
    }: {
      featureKey: string;
      payload: { name?: string; description?: string };
    }) => updateRbacFeature(featureKey, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.rbac.features() });
      toast.success('Module updated');
    },
    onError: (error) => toast.error(getErrorMessage(error, 'Failed to update module')),
  });
}

export function useDeleteRbacFeature() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteRbacFeature,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.rbac.all });
      toast.success('Module deleted');
    },
    onError: (error) => toast.error(getErrorMessage(error, 'Failed to delete module')),
  });
}
